import { createContext, createElement, useCallback, useContext, useEffect, useMemo, useRef, useState, } from 'react';
import { App } from '../antd/app.js';
import { errorMessageFa, isApiError } from '../api/api-error.js';
/** Debounces a changing value — search boxes, mostly. */
export function useDebounced(value, delayMs = 350) {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const timer = window.setTimeout(() => {
            setDebounced(value);
        }, delayMs);
        return () => {
            window.clearTimeout(timer);
        };
    }, [value, delayMs]);
    return debounced;
}
/** `document.title`, restored on unmount. */
export function useDocumentTitle(title, suffix = 'پنل مدیریت بوکورا') {
    useEffect(() => {
        const previous = document.title;
        document.title = title ? `${title} — ${suffix}` : suffix;
        return () => {
            document.title = previous;
        };
    }, [title, suffix]);
}
export function useListQueryState(initialPageSize = 20) {
    const [page, setPageState] = useState(1);
    const [pageSize, setPageSize] = useState(initialPageSize);
    const setPage = useCallback((nextPage, nextSize) => {
        setPageState(nextPage);
        setPageSize(nextSize);
    }, []);
    const resetPage = useCallback(() => {
        setPageState(1);
    }, []);
    return useMemo(() => ({ page, pageSize, setPage, resetPage }), [page, pageSize, setPage, resetPage]);
}
const ApiErrorFormatterContext = createContext(null);
/** Hands `useApiFeedback().showError` the app's own wording (mounted once, at the root). */
export function ApiErrorFormatterProvider({ format, children, }) {
    return createElement(ApiErrorFormatterContext.Provider, { value: format }, children);
}
/**
 * Mutation feedback, centralised so every screen reports failures the same way.
 * Field-level `class-validator` messages are joined into the toast rather than
 * dropped — a 400 that says only «ورودی نامعتبر است» is not actionable.
 */
export function useApiFeedback() {
    const { message } = App.useApp();
    const format = useContext(ApiErrorFormatterContext);
    const showError = useCallback((error) => {
        const fields = isApiError(error) ? error.fieldMessages : [];
        const body = fields.length > 0 ? fields.join('؛ ') : (format?.(error) ?? null) || errorMessageFa(error);
        void message.error(body || 'این کار انجام نشد. دوباره امتحان کنید.');
    }, [message, format]);
    const showSuccess = useCallback((text) => {
        void message.success(text);
    }, [message]);
    return useMemo(() => ({ showError, showSuccess }), [showError, showSuccess]);
}
/**
 * A countdown in seconds, for the OTP resend gate. Ticks on a real clock rather
 * than counting intervals, so a backgrounded tab does not come back with thirty
 * seconds still on the timer.
 *
 * Also resyncs on `visibilitychange`: a backgrounded tab has its `setInterval`
 * throttled (browsers slow it to roughly once a minute, or pause it outright),
 * so without this the number on screen can sit stale for up to a minute after
 * the tab regains focus. Recomputing from the wall-clock deadline the moment the
 * tab becomes visible again closes that gap immediately instead of waiting for
 * the next tick.
 */
export function useCountdown() {
    const [secondsLeft, setSecondsLeft] = useState(0);
    const deadline = useRef(0);
    const start = useCallback((seconds) => {
        deadline.current = Date.now() + seconds * 1000;
        setSecondsLeft(seconds);
    }, []);
    useEffect(() => {
        if (secondsLeft <= 0)
            return;
        const tick = () => {
            const remaining = Math.max(0, Math.ceil((deadline.current - Date.now()) / 1000));
            setSecondsLeft(remaining);
        };
        const timer = window.setInterval(() => {
            tick();
            if (Date.now() >= deadline.current)
                window.clearInterval(timer);
        }, 500);
        const onVisible = () => tick();
        document.addEventListener('visibilitychange', onVisible);
        return () => {
            window.clearInterval(timer);
            document.removeEventListener('visibilitychange', onVisible);
        };
    }, [secondsLeft]);
    return { secondsLeft, start };
}
//# sourceMappingURL=index.js.map